import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "./icons";
import { trackEvent } from "../../lib/analytics";

/**
 * AlternativeOfferBanner
 * Renders a full-width "don't like this option? look at this →" section
 * intended to sit directly above the global <Footer/>. Mirrors the
 * Hero's secondary CTA so visitors get one more chance to switch paths.
 */
export default function AlternativeOfferBanner({
  eyebrow = "Not the right fit?",
  headline,
  subheadline,
  ctaText = "Take Me There",
  ctaTo,
  ctaHref,
  eventName = "alternative_offer_banner_click",
}) {
  if (!headline || (!ctaTo && !ctaHref)) return null;

  const onClick = () => {
    try {
      trackEvent(eventName, {
        metadata: { destination: ctaTo || ctaHref, headline },
      });
    } catch (e) {
      /* noop */
    }
  };

  const buttonClasses =
    "group inline-flex items-center gap-2 px-7 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform";

  return (
    <section
      data-testid="alternative-offer-banner"
      className="relative py-16 md:py-20 border-y border-gold/15 bg-gradient-to-b from-black/70 via-[#080808] to-black/70 overflow-hidden"
    >
      <div className="absolute -inset-20 -z-10 rounded-[3rem] bg-gradient-to-br from-gold/10 via-transparent to-transparent blur-3xl opacity-60" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold/90 px-4 py-1.5 rounded-full border border-gold/30 bg-black/40 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
          {eyebrow}
        </span>

        <h2 className="mt-6 font-display text-[clamp(1.75rem,4vw,2.75rem)] leading-[1.15] tracking-tight">
          {headline}
        </h2>

        {subheadline && (
          <p className="mt-4 text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
            {subheadline}
          </p>
        )}

        <div className="mt-8 flex justify-center">
          {ctaTo ? (
            <Link
              to={ctaTo}
              onClick={onClick}
              data-testid="alternative-offer-cta"
              className={buttonClasses}
            >
              {ctaText}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          ) : (
            <a
              href={ctaHref}
              onClick={onClick}
              data-testid="alternative-offer-cta"
              className={buttonClasses}
            >
              {ctaText}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
